/**
 * `/news` and `/news/:slug` — the dispatch board and one dispatch.
 *
 * The decision that shaped both: **an article page costs at most two requests, ever.** The list
 * endpoint already answers with the twenty newest rows in one call
 * (`{ items, page, pageSize, total }`, `cms-public.controller.ts`), so the article page fetches
 * the article and that same first page in parallel and reads its prev/next neighbours out of it.
 * Nothing walks the archive. An article older than the first page simply has no neighbour links —
 * a missing convenience, not a broken page, and far better than N requests to place two arrows.
 *
 * A 404 is a state, not a fault: an unpublished or deleted dispatch renders "this dispatch is
 * gone" with a way back to the list. The list endpoint answering an empty array (which it does in
 * preview mode) renders a line and the Play free CTA, exactly as the FAQ does.
 *
 * Bodies are admin-authored HTML and go through `sanitizeCmsHtml` before they touch `innerHTML`.
 */
import { icon, plateChrome, type IconName, type PlateChromeOptions } from '../shell/ui';
import { t } from '../i18n';
import { landingFooter, landingTopBar, pageTitle } from './landing';
import {
  articleTimestamp,
  fetchNews,
  fetchNewsArticle,
  formatArticleDate,
  isAborted,
  isNotFound,
  isoDate,
  sanitizeCmsHtml,
  type NewsArticle,
} from './content-api';

type Navigate = (to: string) => void;

const H1 = 'kit-title font-heading uppercase text-[24px] sm:text-[36px] lg:text-[46px] m-0';
const LEDE = 'text-[15px] sm:text-[17px] leading-relaxed text-text-dim max-w-[62ch] m-0';
const CTA_PRIMARY: PlateChromeOptions = { variant: 'gold', size: 'lg', className: 'text-[15px] tracking-[0.12em]' };
const CTA_GHOST: PlateChromeOptions = { variant: 'secondary', size: 'md', className: 'text-[13px] tracking-[0.12em]' };
const MAIN =
  'relative px-5 sm:px-8 lg:px-14 xl:px-24 pt-[calc(96px+env(safe-area-inset-top))] pb-20 lg:pb-28 max-w-[1180px] mx-auto';

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function link(href: string, label: string, className: string, navigate: Navigate): HTMLAnchorElement {
  const a = el('a', className, label);
  a.href = href;
  a.addEventListener('click', (event) => {
    event.preventDefault();
    navigate(href);
  });
  return a;
}

function plateLink(href: string, label: string | Node, opts: PlateChromeOptions, navigate: Navigate): HTMLAnchorElement {
  const a = document.createElement('a');
  a.href = href;
  plateChrome(a, label, { ...opts, className: `no-underline ${opts.className ?? ''}` });
  a.addEventListener('click', (event) => {
    event.preventDefault();
    navigate(href);
  });
  return a;
}

function dateLine(article: NewsArticle, className: string): HTMLElement {
  const stamp = articleTimestamp(article);
  const time = el('time', className, formatArticleDate(stamp));
  const iso = isoDate(stamp);
  if (iso) time.setAttribute('datetime', iso);
  return time;
}

function titlePlate(title: string): HTMLElement {
  const plate = el('div', 'kit-header [--kh:46px] w-fit max-w-full');
  plate.append(el('h2', 'kit-title font-heading uppercase text-[16px] m-0', title));
  return plate;
}

/** A link dressed as a small painted key, with the picture that says where it goes. */
function keyLink(href: string, art: IconName, label: string, navigate: Navigate): HTMLAnchorElement {
  const caption = document.createDocumentFragment();
  caption.append(icon(art, { size: 24 }), document.createTextNode(label));
  return plateLink(href, caption, { variant: 'secondary', size: 'md', className: 'text-[12px] tracking-[0.14em]' }, navigate);
}

function noticeBlock(title: string, line: string, actions: Node[], slot: string): HTMLElement {
  const box = el('div', 'kit-window flex flex-col items-start gap-4');
  box.dataset.slot = slot;
  box.append(
    titlePlate(title),
    el('p', 'text-[15px] leading-relaxed text-text-dim max-w-[52ch] m-0', line),
  );
  if (actions.length) {
    const row = el('div', 'flex flex-wrap gap-3');
    row.append(...actions);
    box.append(row);
  }
  return box;
}

function skeleton(rows: number): HTMLElement {
  const wrap = el('div', 'flex flex-col gap-3');
  wrap.dataset.slot = 'news-loading';
  wrap.setAttribute('aria-busy', 'true');
  for (let i = 0; i < rows; i += 1) wrap.append(el('div', 'kit-plate [--kp-rim:16px] h-[120px] opacity-60 motion-safe:animate-pulse'));
  return wrap;
}

// ── Chrome shared by both routes ────────────────────────────────────────────────────────────

interface PageFrame {
  root: HTMLElement;
  main: HTMLElement;
  revealIn: (scope: HTMLElement) => void;
  dispose: () => void;
}

function pageFrame(app: HTMLElement, navigate: Navigate, view: string): PageFrame {
  const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = el('div', 'landing relative min-h-full bg-bg text-text overflow-x-hidden');
  root.dataset.view = view;
  const main = el('main', MAIN);
  root.append(landingTopBar(navigate), main, landingFooter(navigate));
  app.replaceChildren(root);
  try {
    window.scrollTo(0, 0);
  } catch {
    /* jsdom */
  }

  let observer: IntersectionObserver | null = null;
  if (!reducedMotion && typeof IntersectionObserver === 'function') {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            observer?.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 },
    );
  }
  const revealIn = (scope: HTMLElement): void => {
    for (const node of scope.querySelectorAll<HTMLElement>('.reveal')) {
      if (observer) observer.observe(node);
      else node.classList.add('is-in');
    }
  };

  return {
    root,
    main,
    revealIn,
    dispose: () => {
      observer?.disconnect();
      root.remove();
    },
  };
}

// ── /news ───────────────────────────────────────────────────────────────────────────────────

function articleCard(article: NewsArticle, navigate: Navigate): HTMLElement {
  const href = `/news/${article.slug}`;
  const card = el(
    'article',
    'reveal kit-plate [--kp-rim:18px] [--kp-pad:16px] flex flex-col sm:flex-row gap-4',
  );
  card.dataset.slot = 'news-card';

  if (article.imageUrl) {
    const figure = el('div', 'kit-well relative sm:w-[240px] lg:w-[300px] shrink-0 aspect-[16/9] sm:aspect-auto overflow-hidden');
    const img = el('img', 'absolute inset-0 w-full h-full object-cover');
    img.src = article.imageUrl;
    img.alt = '';
    img.loading = 'lazy';
    figure.append(img);
    card.append(figure);
  }

  const body = el('div', 'flex flex-col gap-2 min-w-0 flex-1');
  const meta = el('div', 'flex items-center gap-3 flex-wrap');
  meta.append(dateLine(article, 'font-heading uppercase tracking-[0.16em] text-[11px] text-text-dim'));
  if (article.tag) {
    const tag = el('span', 'kit-chip [--kh:26px] font-heading uppercase tracking-[0.12em] text-[11px]');
    tag.append(icon('star', { size: 20 }), article.tag);
    meta.append(tag);
  }
  body.append(meta);

  body.append(link(
    href,
    article.title,
    'kit-title font-heading uppercase text-[18px] sm:text-[21px] no-underline min-h-[44px] flex items-center',
    navigate,
  ));
  if (article.summary) {
    body.append(el('p', 'text-[14px] sm:text-[15px] leading-relaxed text-text-dim m-0 max-w-[70ch]', article.summary));
  }
  const read = keyLink(href, 'next', t('news.readDispatch'), navigate);
  read.classList.add('mt-auto', 'self-start');
  body.append(read);
  card.append(body);
  return card;
}

export function renderNewsPage(app: HTMLElement, navigate: Navigate): () => void {
  const frame = pageFrame(app, navigate, 'news');
  const { main } = frame;

  const head = el('header', 'flex flex-col gap-4 mb-10 lg:mb-14');
  head.append(pageTitle('bell', el('h1', H1, t('news.title'))), el('p', LEDE, t('news.lede')));
  main.append(head);

  const list = el('div', 'flex flex-col gap-3 sm:gap-4');
  list.dataset.slot = 'news-list';
  list.append(skeleton(4));
  main.append(list);

  const moreSlot = el('div', 'flex justify-center pt-8');
  main.append(moreSlot);

  const controller = new AbortController();
  let loaded: NewsArticle[] = [];

  const renderMore = (page: number, total: number): void => {
    moreSlot.replaceChildren();
    if (loaded.length >= total) return;
    const more = document.createElement('button');
    more.type = 'button';
    more.dataset.slot = 'news-more';
    plateChrome(more, t('news.olderDispatches'), CTA_GHOST);
    more.addEventListener('click', () => {
      more.disabled = true;
      void load(page + 1);
    });
    moreSlot.append(more);
  };

  const load = async (page: number): Promise<void> => {
    try {
      const result = await fetchNews(page, { signal: controller.signal });
      if (controller.signal.aborted) return;
      // The server orders by `publishedAt desc`; sorting again keeps the page right even if a
      // row has no publishedAt (draft promoted late) and the endpoint's order surprises us.
      const merged = [...loaded, ...result.items].sort(
        (a, b) => Date.parse(isoDate(articleTimestamp(b)) || '0') - Date.parse(isoDate(articleTimestamp(a)) || '0'),
      );
      loaded = merged;
      if (loaded.length === 0) {
        list.replaceChildren(
          noticeBlock(
            t('news.emptyTitle'),
            t('news.emptyBody'),
            [plateLink('/register', t('landing.hero.playFree'), CTA_PRIMARY, navigate)],
            'news-empty',
          ),
        );
        moreSlot.replaceChildren();
        return;
      }
      list.replaceChildren(...loaded.map((article) => articleCard(article, navigate)));
      frame.revealIn(list);
      renderMore(result.page, result.total);
    } catch (error) {
      if (isAborted(error) || controller.signal.aborted) return;
      list.replaceChildren(
        noticeBlock(
          t('news.offlineTitle'),
          t('news.offlineBody'),
          [plateLink('/register', t('landing.hero.playFree'), CTA_PRIMARY, navigate)],
          'news-error',
        ),
      );
      moreSlot.replaceChildren();
    }
  };

  void load(1);

  return () => {
    controller.abort();
    frame.dispose();
  };
}

// ── /news/:slug ─────────────────────────────────────────────────────────────────────────────

function neighbourNav(prev: NewsArticle | null, next: NewsArticle | null, navigate: Navigate): HTMLElement {
  const nav = el('nav', 'grid gap-3 sm:grid-cols-2 pt-8 mt-8');
  nav.dataset.slot = 'article-neighbours';
  nav.setAttribute('aria-label', t('news.neighbours'));
  const cell = (article: NewsArticle | null, art: IconName, label: string): HTMLElement => {
    if (!article) return el('div', '');
    const a = link(
      `/news/${article.slug}`,
      '',
      'kit-plate kit-plate-hit [--kp-rim:16px] [--kp-pad:12px_14px] flex items-center gap-3 min-h-[72px] no-underline',
      navigate,
    );
    const words = el('span', 'flex flex-col gap-1 min-w-0');
    words.append(
      el('span', 'font-heading uppercase tracking-[0.18em] text-[11px] text-text-dim', label),
      el('span', 'kit-label font-heading uppercase text-[14px] leading-snug', article.title),
    );
    a.append(icon(art, { size: 40 }), words);
    return a;
  };
  nav.append(cell(next, 'prev', t('news.newer')), cell(prev, 'next', t('news.older')));
  return nav;
}

export function renderArticlePage(app: HTMLElement, navigate: Navigate, slug: string): () => void {
  const frame = pageFrame(app, navigate, 'article');
  const { main } = frame;

  const back = keyLink('/news', 'prev', t('news.allDispatches'), navigate);
  back.classList.add('mb-6');
  main.append(back);

  const holder = el('div', 'flex flex-col');
  holder.dataset.slot = 'article-body';
  holder.append(skeleton(1));
  main.append(holder);

  const controller = new AbortController();

  void (async () => {
    // Two requests, in parallel, and the second one is allowed to fail: the neighbour links are
    // a convenience and the article is the page.
    const articlePromise = fetchNewsArticle(slug, { signal: controller.signal });
    const listPromise = fetchNews(1, { signal: controller.signal }).catch(() => null);
    try {
      const article = await articlePromise;
      if (controller.signal.aborted) return;

      const header = el('header', 'flex flex-col gap-4 mb-8');
      header.append(
        dateLine(article, 'font-heading uppercase tracking-[0.2em] text-[11px] text-text-dim'),
        pageTitle('bell', el('h1', H1, article.title)),
      );
      if (article.summary) header.append(el('p', LEDE, article.summary));

      const nodes: Node[] = [header];

      if (article.imageUrl) {
        const figure = el('figure', 'kit-plate [--kp-rim:18px] [--kp-pad:14px] m-0 mb-8');
        const frame = el('div', 'kit-well relative overflow-hidden aspect-[16/9]');
        const img = el('img', 'absolute inset-0 w-full h-full object-cover');
        img.src = article.imageUrl;
        img.alt = '';
        frame.append(img);
        figure.append(frame);
        nodes.push(figure);
      }

      // Admin-authored HTML: sanitised, then set as markup so the prose keeps its structure.
      const body = el(
        'div',
        'kit-window text-[15px] sm:text-[16px] leading-relaxed text-text max-w-[84ch] [&_h2]:font-heading [&_h2]:uppercase [&_h2]:tracking-[0.08em] [&_h2]:text-[20px] [&_h2]:text-white [&_h2]:mt-8 [&_h2]:mb-2 [&_h3]:font-heading [&_h3]:uppercase [&_h3]:text-[16px] [&_h3]:text-white [&_h3]:mt-6 [&_p]:my-3 [&_a]:text-cyan [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:my-1 [&_ul_li]:list-disc [&_ol_li]:list-decimal [&_strong]:text-text [&_img]:max-w-full [&_img]:h-auto [&_table]:block [&_table]:overflow-x-auto [&_pre]:overflow-x-auto',
      );
      body.dataset.slot = 'article-content';
      body.innerHTML = sanitizeCmsHtml(article.content ?? '');
      nodes.push(body);

      const list = await listPromise;
      if (controller.signal.aborted) return;
      const items = list?.items ?? [];
      const index = items.findIndex((row) => row.slug === article.slug);
      if (index !== -1) {
        // The list is newest-first: the previous index is newer, the next one is older.
        const newer = index > 0 ? items[index - 1]! : null;
        const older = index < items.length - 1 ? items[index + 1]! : null;
        if (newer || older) nodes.push(neighbourNav(older, newer, navigate));
      }

      nodes.push(
        (() => {
          const foot = el('div', 'flex flex-wrap gap-3 pt-10');
          foot.append(
            plateLink('/register', t('landing.hero.playFree'), CTA_PRIMARY, navigate),
            keyLink('/news', 'prev', t('news.backToNews'), navigate),
          );
          return foot;
        })(),
      );

      holder.replaceChildren(...nodes);
      frame.revealIn(holder);
    } catch (error) {
      if (isAborted(error) || controller.signal.aborted) return;
      const gone = isNotFound(error);
      holder.replaceChildren(
        noticeBlock(
          gone ? t('news.goneTitle') : t('news.loadFailedTitle'),
          gone ? t('news.goneBody') : t('news.offlineBody'),
          [plateLink('/news', t('news.backToNews'), CTA_PRIMARY, navigate)],
          gone ? 'article-missing' : 'article-error',
        ),
      );
    }
  })();

  return () => {
    controller.abort();
    frame.dispose();
  };
}
